interface Props {
  micOn: boolean;
  camOn: boolean;
  screenSharing: boolean;
  panel: 'none' | 'notes' | 'files' | 'people';
  onToggleMic: () => void;
  onToggleCam: () => void;
  onToggleScreen: () => void;
  onTogglePanel: (p: 'none' | 'notes' | 'files' | 'people') => void;
  onToggleTest?: () => void;
  onLeave: () => void;
  /** Affiche le bouton « lever la main » (invité mis en sourdine par l'hôte). */
  showHand?: boolean;
  handRaised?: boolean;
  onToggleHand?: () => void;
}

/**
 * Barre de contrôles de la salle ZCall.
 * Trois groupes stables : média à gauche, outils au centre, sortie à droite.
 * Chaque groupe reste aligné, rien ne se mélange même sur petit écran.
 */
export default function CallControls({
  micOn, camOn, screenSharing, panel,
  onToggleMic, onToggleCam, onToggleScreen, onTogglePanel, onToggleTest, onLeave,
  showHand, handRaised, onToggleHand,
}: Props) {
  const base =
    'w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center cursor-pointer transition-colors text-lg flex-shrink-0';
  const on = 'bg-white/10 text-white hover:bg-white/20';
  const off = 'bg-red-500 text-white hover:bg-red-600';
  const active = 'bg-accent-500 text-white hover:bg-accent-600';

  return (
    <div className="w-full px-3 py-2.5 md:py-3">
      <div className="flex items-center justify-center gap-2">
        {/* ── Média ── */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onToggleMic}
            className={`${base} ${micOn ? on : off}`}
            title={micOn ? 'Couper le micro' : 'Activer le micro'}
            aria-label={micOn ? 'Couper le micro' : 'Activer le micro'}
          >
            <i className={micOn ? 'ri-mic-line' : 'ri-mic-off-line'}></i>
          </button>
          <button
            type="button"
            onClick={onToggleCam}
            className={`${base} ${camOn ? on : off}`}
            title={camOn ? 'Couper la caméra' : 'Activer la caméra'}
            aria-label={camOn ? 'Couper la caméra' : 'Activer la caméra'}
          >
            <i className={camOn ? 'ri-vidicon-line' : 'ri-camera-off-line'}></i>
          </button>
          <button
            type="button"
            onClick={onToggleScreen}
            className={`${base} ${screenSharing ? active : on}`}
            title={screenSharing ? "Arrêter le partage d'écran" : 'Partager mon écran'}
            aria-label="Partager mon écran"
          >
            <i className="ri-computer-line"></i>
          </button>
        </div>

        <span className="w-px h-6 bg-white/15 mx-0.5 flex-shrink-0"></span>

        {/* ── Outils de réunion ── */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onTogglePanel(panel === 'notes' ? 'none' : 'notes')}
            className={`${base} ${panel === 'notes' ? active : on}`}
            title="Bloc-notes de la réunion"
            aria-label="Bloc-notes"
          >
            <i className="ri-sticky-note-line"></i>
          </button>
          <button
            type="button"
            onClick={() => onTogglePanel(panel === 'files' ? 'none' : 'files')}
            className={`${base} ${panel === 'files' ? active : on}`}
            title="Dossier partagé"
            aria-label="Dossier partagé"
          >
            <i className="ri-folder-shared-line"></i>
          </button>
          <button
            type="button"
            onClick={() => onTogglePanel(panel === 'people' ? 'none' : 'people')}
            className={`${base} ${panel === 'people' ? active : on}`}
            title="Participants et qualité réseau"
            aria-label="Participants"
          >
            <i className="ri-group-line"></i>
          </button>
          {onToggleTest && (
            <button
              type="button"
              onClick={onToggleTest}
              className={`${base} ${on}`}
              title="Tester la caméra et choisir le matériel"
              aria-label="Tester la caméra"
            >
              <i className="ri-settings-3-line"></i>
            </button>
          )}
        </div>

        {showHand && onToggleHand && (
          <>
            <span className="w-px h-6 bg-white/15 mx-0.5 flex-shrink-0"></span>
            <button
              type="button"
              onClick={onToggleHand}
              className={`h-10 md:h-11 px-3 md:px-4 rounded-full flex items-center gap-1.5 text-xs md:text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors flex-shrink-0 ${
                handRaised ? 'bg-amber-400 text-foreground-950 hover:bg-amber-300' : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              title="Demander le droit de présenter"
            >
              <i className="ri-arrow-up-circle-line"></i>
              {handRaised ? 'Main levée' : 'Lever la main'}
            </button>
          </>
        )}

        <span className="w-px h-6 bg-white/15 mx-0.5 flex-shrink-0"></span>

        {/* ── Sortie ── */}
        <button
          type="button"
          onClick={onLeave}
          className="h-10 md:h-11 px-4 md:px-5 rounded-full flex items-center gap-1.5 bg-red-500 text-white text-xs md:text-sm font-semibold whitespace-nowrap hover:bg-red-600 transition-colors cursor-pointer flex-shrink-0"
          title="Quitter la réunion"
        >
          <i className="ri-close-line"></i>
          Quitter
        </button>
      </div>
    </div>
  );
}